import { EASYLEASE } from './easylease'
export type SellerType = 'personal' | 'business'
export type VerificationStatus = 'todo' | 'pending' | 'verified' | 'attention'
export type ThemePresetId = 'auto' | 'clean' | 'premium' | 'bold' | 'minimal'
export type BackgroundStyle = 'clean' | 'gradient' | 'tint' | 'custom'
/** Predefined canvas treatments a merchant can pick for their storefront. */
export type BackdropStyle = 'plain' | 'dots' | 'grid' | 'lines' | 'rings'
export type ButtonShape = 'rounded' | 'soft' | 'square'
export type LogoDisplay = 'logo' | 'logo-name'
export type HeroStyle = 'minimal' | 'brand' | 'image'
export type ChatBackground = 'white' | 'tint'
export type FeaturedMode = 'auto' | 'manual' | 'hidden'
export type AiTone = 'Professional' | 'Friendly' | 'Premium' | 'Casual'
export type AiLanguage = 'English' | 'Arabic' | 'Automatic'
export interface ShopTheme {
  preset: ThemePresetId
  primary: string
  accent: string
  background: string
  backgroundStyle: BackgroundStyle
  /** The canvas treatment drawn over the background colour. */
  backdrop: BackdropStyle
  /** 0–100. How strongly the brand tint shows through the background. */
  backgroundStrength: number
  buttonShape: ButtonShape
  /** When true, buttons and chat simply follow the brand colours above. */
  useBrandAutomatically: boolean
  /** Empty string means "inherit from brand". */
  chatButtonColor: string
  chatAccentColor: string
  chatBackground: ChatBackground
}
export type HeroEmphasis = 'none' | 'underline' | 'highlight' | 'colour'
export interface ShopHero {
  headline: string
  subtitle: string
  logoDisplay: LogoDisplay
  style: HeroStyle
  /** How the last word of the headline is emphasised. */
  emphasis: HeroEmphasis
  /** 0–100. Thickness of the underline, or height of the highlight. */
  emphasisWeight: number
}
export interface SuggestedQuestion {
  id: string
  text: string
}
export interface ShopHighlight {
  id: string
  icon: string
  title: string
  detail: string
}
export interface ShopCategoryToggle {
  name: string
  visible: boolean
}
export interface ShopContentConfig {
  questions: SuggestedQuestion[]
  highlights: ShopHighlight[]
  featuredMode: FeaturedMode
  /** Curated product ids, in display order. Only used in 'manual' mode. */
  featuredIds: string[]
  categories: ShopCategoryToggle[]
  /**
   * Translations for anything the merchant wrote, keyed by language code then
   * by field key (`<questionId>`, `<highlightId>.title`, `<highlightId>.detail`,
   * `hero.headline`, `hero.subtitle`). The default language lives in the
   * fields above; this only holds the other enabled languages.
   */
  i18n: Record<string, Record<string, string>>
}
export interface ShopLanguage {
  code: string
  /** English name shown in Settings, e.g. "Arabic". */
  name: string
  /** How it reads to a customer, e.g. "العربية". */
  nativeName: string
  rtl?: boolean
}
/** Languages a merchant can switch on for their storefront. */
export const AVAILABLE_LANGUAGES: ShopLanguage[] = [
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
  },
  {
    code: 'ar',
    name: 'Arabic',
    nativeName: 'العربية',
    rtl: true,
  },
  {
    code: 'fr',
    name: 'French',
    nativeName: 'Français',
  },
  {
    code: 'ur',
    name: 'Urdu',
    nativeName: 'اردو',
    rtl: true,
  },
  {
    code: 'hi',
    name: 'Hindi',
    nativeName: 'हिन्दी',
  },
  {
    code: 'ru',
    name: 'Russian',
    nativeName: 'Русский',
  },
  {
    code: 'zh',
    name: 'Chinese',
    nativeName: '中文',
  },
]
export interface AiExperienceConfig {
  tone: AiTone
  language: AiLanguage
  greeting: string
  capabilities: Record<string, boolean>
}
export interface ShopLocation {
  id: string
  name: string
  address: string
  mapPin: string
}
export interface ContactConfig {
  email: string
  phone: string
  whatsapp: string
  showEmail: boolean
  showPhone: boolean
  showWhatsapp: boolean
}
export interface VerificationConfig {
  identity: VerificationStatus
  license: VerificationStatus
  review: VerificationStatus
  licenseNumber: string
  licenseExpiry: string
}
/**
 * How the shop reads on Google and in shared links. Talab generates the
 * technical side (canonical URLs, sitemap, Open Graph, structured data) from
 * the shop and catalogue — the merchant only supplies business-friendly words.
 */
export interface ShopSeoConfig {
  /** Headline shown in search results. */
  title: string
  /** The two lines beneath it. */
  description: string
  /** Plain topics customers might search for — never called keywords. */
  topics: string[]
  /** Image used when the link is shared. Empty falls back to the logo. */
  shareImage: string
  /** Whether search engines may list the shop at all. */
  indexable: boolean
}
export interface ShopConfig {
  sellerType: SellerType
  logo: string
  legalName: string
  publicName: string
  handle: string
  businessType: string
  about: string
  website: string
  verification: VerificationConfig
  countries: string[]
  locations: ShopLocation[]
  contact: ContactConfig
  theme: ShopTheme
  hero: ShopHero
  content: ShopContentConfig
  ai: AiExperienceConfig
  published: boolean
  /** Talab mark in the shop header. Removing it requires the Scale plan. */
  showTalabBranding: boolean
  /** Country selector in the shop header. */
  showCountrySelector: boolean
  /** The merchant's own domain, once connected. Empty until then. */
  customDomain: string
  /** Language codes offered on the storefront, in display order. */
  languages: string[]
  /** The language the shop content above is written in. */
  defaultLanguage: string
  seo: ShopSeoConfig
}
export const AI_CAPABILITIES = [
  'Find products',
  'Compare products',
  'Answer questions',
  'Check availability',
  'Generate leads',
  'Help customers choose',
  'Delivery questions',
  'Pricing questions',
]
export const HIGHLIGHT_ICONS = [
  'shield',
  'truck',
  'support',
  'clock',
  'award',
  'wallet',
  'map',
  'sparkles',
]
/** The EasyLease sample merchant — proves the builder can recreate the real shop. */
export const easyleaseConfig: ShopConfig = {
  sellerType: 'business',
  logo: EASYLEASE.logo,
  legalName: 'Easylease Vehicle Rental LLC',
  publicName: 'EasyLease',
  handle: 'easylease',
  businessType: 'Vehicle Rental',
  about:
    'EasyLease is a UAE mobility partner offering short-term rentals and long-term leasing across sedans, SUVs, luxury cars, vans and commercial fleets — with maintenance, delivery and 24/7 roadside support included.',
  website: 'https://easylease.ae',
  verification: {
    identity: 'verified',
    license: 'pending',
    review: 'todo',
    licenseNumber: 'CN-1094552',
    licenseExpiry: '14 Mar 2027',
  },
  countries: ['United Arab Emirates', 'Saudi Arabia'],
  locations: [
    {
      id: 'l1',
      name: 'Dubai Branch',
      address: 'Al Quoz, Dubai',
      mapPin: '25.1193, 55.2010',
    },
    {
      id: 'l2',
      name: 'Abu Dhabi Branch',
      address: 'Mussafah, Abu Dhabi',
      mapPin: '24.3573, 54.5136',
    },
  ],
  contact: {
    email: 'business@easylease.ae',
    phone: '+971 4 000 0000',
    whatsapp: '+971 50 000 0000',
    showEmail: true,
    showPhone: true,
    showWhatsapp: true,
  },
  theme: {
    preset: 'auto',
    primary: '#4C7FC1',
    accent: '#F5CE3E',
    background: '#F4F7FC',
    backgroundStyle: 'tint',
    backdrop: 'plain',
    backgroundStrength: 45,
    buttonShape: 'soft',
    useBrandAutomatically: true,
    chatButtonColor: '',
    chatAccentColor: '',
    chatBackground: 'white',
  },
  hero: {
    headline: 'Ask EasyLease Anything',
    subtitle:
      'Find, compare and rent the right vehicle with AI — from sedans and SUVs to luxury cars, vans and commercial fleets.',
    logoDisplay: 'logo',
    style: 'brand',
    emphasis: 'underline',
    emphasisWeight: 45,
  },
  content: {
    questions: [
      {
        id: 'q1',
        text: 'Show me available SUVs',
      },
      {
        id: 'q2',
        text: 'What are your monthly rental rates?',
      },
      {
        id: 'q3',
        text: 'I need airport pickup and delivery',
      },
      {
        id: 'q4',
        text: 'Show me luxury cars available today',
      },
    ],
    highlights: [
      {
        id: 'h1',
        icon: 'shield',
        title: 'Fully Insured',
        detail: 'Comprehensive cover on every vehicle',
      },
      {
        id: 'h2',
        icon: 'truck',
        title: 'Free Delivery',
        detail: 'Door-to-door across Dubai',
      },
      {
        id: 'h3',
        icon: 'support',
        title: '24/7 Support',
        detail: 'Roadside help around the clock',
      },
      {
        id: 'h4',
        icon: 'clock',
        title: 'Same-Day Pickup',
        detail: 'Collect within hours of booking',
      },
    ],
    featuredMode: 'auto',
    featuredIds: ['prado', 'landcruiser', 'hiace', 'camry'],
    i18n: {
      ar: {
        q1: 'أرني سيارات الدفع الرباعي المتوفرة',
        q2: 'ما هي أسعار الإيجار الشهري؟',
        'h1.title': 'تأمين شامل',
      },
    },
    categories: [
      {
        name: 'Sedans',
        visible: true,
      },
      {
        name: 'SUVs & 4WDs',
        visible: true,
      },
      {
        name: 'Luxury',
        visible: true,
      },
      {
        name: 'Vans',
        visible: true,
      },
      {
        name: 'Pickups',
        visible: true,
      },
      {
        name: 'Buses',
        visible: false,
      },
      {
        name: 'Electric & Hybrid',
        visible: true,
      },
    ],
  },
  ai: {
    tone: 'Professional',
    language: 'Automatic',
    greeting: 'Hi! How can I help you today?',
    capabilities: AI_CAPABILITIES.reduce<Record<string, boolean>>((acc, c) => {
      acc[c] = c !== 'Generate leads' ? true : true
      return acc
    }, {}),
  },
  published: false,
  showTalabBranding: true,
  showCountrySelector: true,
  customDomain: '',
  languages: ['en', 'ar'],
  defaultLanguage: 'en',
  seo: {
    title: 'EasyLease | Car Rental & Leasing in UAE',
    description:
      'Rent SUVs, sedans and luxury vehicles from EasyLease across Dubai and Abu Dhabi. Daily, weekly and monthly rental options available.',
    topics: [],
    shareImage: '',
    indexable: true,
  },
}
export const BUSINESS_TYPE_OPTIONS = [
  'Vehicle Rental',
  'Car Dealership',
  'Real Estate Agency',
  'Electronics',
  'Fashion',
  'Restaurant',
  'Services',
  'Recruitment',
  'Travel',
  'Other',
]
export const COUNTRY_OPTIONS = [
  'United Arab Emirates',
  'Saudi Arabia',
  'Qatar',
  'Kuwait',
  'Bahrain',
  'Oman',
  'Egypt',
  'Jordan',
]
